import { useEffect, useState } from "react";
import AddBlogForm from "./AddBlogForm";
import { Blog } from "@/types";
import { Button } from "./ui/button";
import { Pencil, Trash2 } from "lucide-react";
import { BlogRepository } from "@/infrastructure/repositories/BlogRepository";

const PAGE_SIZE = 10;

type FeedbackType = "success" | "error";

export function CreateBlogPage() {
  const blogRepo = new BlogRepository();

  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
  const [blogToDelete, setBlogToDelete] = useState<Blog | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [feedback, setFeedback] = useState<{
    type: FeedbackType;
    message: string;
  } | null>(null);

  // ----------------------------------
  // BLOG MAPPER
  // ----------------------------------
  const mapApiBlogToBlog = (apiBlog: any): Blog => ({
    id: Number(apiBlog.post_id),
    title: apiBlog.post_title ?? "",
    slug: apiBlog.post_slug ?? "",
    excerpt: apiBlog.post_meta_desc ?? "",
    content: apiBlog.post_desc ?? "",
    image: apiBlog.post_img
      ? `https://api.swimpractice.com/uploads/blog/${apiBlog.post_img}`
      : "",
    category: String(apiBlog.post_category ?? ""),
    featured: String(apiBlog.post_category) === "19",
    readTime: apiBlog.read_time ? String(apiBlog.read_time) : "5 min read",
    author: apiBlog.author ?? "Admin",
    date: apiBlog.post_created_at
      ? new Date(Number(apiBlog.post_created_at) * 1000).toLocaleDateString()
      : "",
  });

  // ----------------------------------
  // INITIAL LOAD
  // ----------------------------------
  useEffect(() => {
    const loadInitialBlogs = async () => {
      setLoading(true);

      const apiBlogs = await blogRepo.getBlogList(1, PAGE_SIZE);
      setBlogs(apiBlogs.map(mapApiBlogToBlog));
      setPage(2);
      setHasMore(apiBlogs.length === PAGE_SIZE);

      setLoading(false);
    };

    loadInitialBlogs();
  }, []);

  // ----------------------------------
  // LOAD MORE ON SCROLL
  // ----------------------------------
  const loadMoreBlogs = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);

    const apiBlogs = await blogRepo.getBlogList(page, PAGE_SIZE);
    setBlogs(prev => [...prev, ...apiBlogs.map(mapApiBlogToBlog)]);
    setPage(prev => prev + 1);

    if (apiBlogs.length < PAGE_SIZE) setHasMore(false);

    setLoadingMore(false);
  };

  useEffect(() => {
    const onScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 300
      ) {
        loadMoreBlogs();
      }
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [page, hasMore, loadingMore]);

  // ----------------------------------
  // CONFIRM DELETE
  // ----------------------------------
  const confirmDelete = async () => {
    if (!blogToDelete) return;

    setSaving(true);

    const success = await blogRepo.deleteBlog(blogToDelete.id);

    if (success) {
      setBlogs(prev => prev.filter(b => b.id !== blogToDelete.id));
      setFeedback({
        type: "success",
        message: "Blog deleted successfully",
      });
    } else {
      setFeedback({
        type: "error",
        message: "Failed to delete blog",
      });
    }

    setBlogToDelete(null);
    setSaving(false);
  };

  // ----------------------------------
  // CREATE / UPDATE
  // ----------------------------------
 const handleSubmit = async (blog: Blog) => {
  setSaving(true);

  const normalized: Blog = {
  ...blog,
  category: blog.featured ? "19" : "20",
  createdBy: blog.createdBy ?? 8419,
  status: "published",
  readTime: blog.readTime || "5 min read",
  date: blog.date || new Date().toLocaleDateString(),
};


  let success = false;

  if (editingBlog) {
    success = await blogRepo.updateBlogV2(normalized);

    if (success) {
      setBlogs(prev =>
        prev.map(b =>
          b.id === blog.id
            ? { ...b, ...normalized }
            : b
        )
      );
      setFeedback({
        type: "success",
        message: "Blog updated successfully",
      });
    }
  } else {
    success = await blogRepo.createBlogV2(normalized);

    if (success) {
      const apiBlogs = await blogRepo.getBlogList(1, PAGE_SIZE);
      setBlogs(apiBlogs.map(mapApiBlogToBlog));
      setPage(2);
      setHasMore(true);

      setFeedback({
        type: "success",
        message: "Blog created successfully",
      });
    }
  }

  if (!success) {
    setFeedback({
      type: "error",
      message: "Failed to save blog",
    });
  }

  setEditingBlog(null);
  setIsAddOpen(false);
  setSaving(false);
};


  // ----------------------------------
  // UI
  // ----------------------------------
  return (
    <div className="max-w-7xl mx-auto px-6 py-10 text-foreground">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold">Blogs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage all blog content.
          </p>
        </div>

        <Button onClick={() => setIsAddOpen(true)} disabled={saving}>
          Add Blog
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground">
          Loading blogs...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogs.map(blog => (
            <div
              key={blog.id}
              className="bg-background border rounded-xl overflow-hidden hover:shadow-lg transition flex flex-col"
            >
              <img
                src={blog.image}
                alt={blog.title}
                className="h-40 w-full object-cover"
              />

              <div className="p-4 flex flex-col flex-1">
                <h3 className="font-semibold line-clamp-2">
                  {blog.title}
                </h3>

                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                  {blog.excerpt}
                </p>

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setEditingBlog(blog);
                      setIsAddOpen(true);
                    }}
                    className="p-2 rounded-md border hover:bg-muted"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => setBlogToDelete(blog)}
                    className="p-2 rounded-md border hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {loadingMore && (
        <div className="text-center text-muted-foreground mt-6">
          Loading more blogs...
        </div>
      )}

      {!hasMore && (
        <div className="text-center text-muted-foreground mt-6 text-sm">
          No more blogs to load
        </div>
      )}

      {/* DELETE CONFIRM DIALOG */}
      {blogToDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-background rounded-2xl border shadow-xl p-6">
            <h3 className="text-lg font-semibold mb-3">
              Delete blog
            </h3>

            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to delete{" "}
              <span className="font-medium text-foreground">
                {blogToDelete.title}
              </span>
              ? This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setBlogToDelete(null)}
                disabled={saving}
              >
                Cancel
              </Button>

              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={saving}
              >
                {saving ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS / ERROR DIALOG */}
      {feedback && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-background rounded-2xl border shadow-xl p-6 text-center">
            <div
              className={`mx-auto mb-4 h-12 w-12 rounded-full flex items-center justify-center ${
                feedback.type === "success"
                  ? "bg-green-500/10 text-green-600"
                  : "bg-red-500/10 text-red-600"
              }`}
            >
              {feedback.type === "success" ? "✓" : "!"}
            </div>

            <h3 className="text-lg font-semibold mb-2">
              {feedback.type === "success" ? "Success" : "Error"}
            </h3>

            <p className="text-sm text-muted-foreground mb-6">
              {feedback.message}
            </p>

            <Button onClick={() => setFeedback(null)} className="w-full">
              OK
            </Button>
          </div>
        </div>
      )}

      {/* ADD / EDIT MODAL */}
      {isAddOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => {
            setIsAddOpen(false);
            setEditingBlog(null);
          }}
        >
          <div
            className="w-full max-w-xl max-h-[90vh] bg-background rounded-2xl border shadow-xl flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-semibold">
                {editingBlog ? "Edit Blog" : "Add New Blog"}
              </h3>

              <button
                onClick={() => {
                  setIsAddOpen(false);
                  setEditingBlog(null);
                }}
                className="text-muted-foreground hover:text-foreground text-xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              <AddBlogForm
                key={editingBlog?.id ?? "new"}
                initialBlog={editingBlog}
                loading={saving}
                onCancel={() => {
                  setEditingBlog(null);
                  setIsAddOpen(false);
                }}
                onSubmit={handleSubmit}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
